import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { InternalEnrichedFinding } from '@terra/ui-design-system';
import type { TargetTypeType } from '@terra/shared-schemas-orchestrator';
import { ARCHIVED_ASSETS_LIFECYCLE_ENABLED_FLAG } from '@terra/shared-contracts-feature-flags';
import {
  RELATED_RECORDS_LIFECYCLE_STATE,
  type LifecycleStateFilterDto,
} from '@terra/shared-dto-types';
import { terraProxyApiClient } from '../../clients/testerClient';
import { isAppTarget } from '../../utils/target-scope.utils';
import { useFeatureFlags } from '../useFeatureFlags';
import { PaginationDefaults } from '@terra/shared-dto-types';
import { fetchFindingsListEnrichedPageForTarget } from './target-findings-api';

export const FINDINGS_BY_SIGNAL_ID_QUERY_KEY = 'findings-by-signal-id';

const EMPTY_FINDINGS: InternalEnrichedFinding[] = [];
const FINDINGS_BY_SIGNAL_MAX_PAGES = 10;

export async function fetchFindingsBySignalIdForTarget({
  targetId,
  targetType,
  signalId,
  findingIds,
  lifecycleState,
}: {
  targetId: string;
  targetType: TargetTypeType;
  signalId?: string | null;
  findingIds?: string[];
  lifecycleState?: LifecycleStateFilterDto;
}): Promise<InternalEnrichedFinding[]> {
  const hasFindingIds = !!findingIds && findingIds.length > 0;

  if (hasFindingIds && findingIds) {
    if (isAppTarget(targetType)) {
      const { findings } = await fetchFindingsListEnrichedPageForTarget({
        targetId,
        targetType,
        options: {
          findingIds,
          limit: PaginationDefaults.MAX_LIMIT,
          ...(lifecycleState ? { lifecycleState } : {}),
        },
      });

      return findings;
    }

    const responses = await Promise.all(
      findingIds.map((findingId) =>
        terraProxyApiClient.getFindingById({ findingId, targetId }),
      ),
    );

    return responses.flatMap((response) => {
      if (response.status !== 200) {
        return [];
      }

      return [response.body as InternalEnrichedFinding];
    });
  }

  if (signalId) {
    const accumulated: InternalEnrichedFinding[] = [];
    let skip = 0;

    for (let page = 0; page < FINDINGS_BY_SIGNAL_MAX_PAGES; page += 1) {
      const { findings, total } = await fetchFindingsListEnrichedPageForTarget({
        targetId,
        targetType,
        options: {
          signalId,
          skip,
          ...(lifecycleState ? { lifecycleState } : {}),
        },
      });

      accumulated.push(...findings);

      if (accumulated.length >= total || findings.length === 0) {
        return accumulated;
      }

      skip += findings.length;
    }

    return accumulated;
  }

  return EMPTY_FINDINGS;
}

export const useFindingsBySignalId = ({
  targetId,
  targetType,
  signalId,
  findingIds,
  enabled = true,
}: {
  targetId: string | null | undefined;
  targetType: TargetTypeType;
  signalId: string | null | undefined;
  findingIds?: string[];
  enabled?: boolean;
}) => {
  const { isFeatureFlagEnabled: isLifecycleEnabled } = useFeatureFlags(
    ARCHIVED_ASSETS_LIFECYCLE_ENABLED_FLAG,
  );
  const lifecycleState = isLifecycleEnabled
    ? RELATED_RECORDS_LIFECYCLE_STATE
    : undefined;
  const hasFindingIds = !!findingIds && findingIds.length > 0;
  const findingIdsCacheKey =
    hasFindingIds && findingIds ? findingIds.join(',') : null;
  const signalIdFilter = hasFindingIds ? null : (signalId ?? null);
  const hasFilter = hasFindingIds || !!signalIdFilter;

  const result = useQuery({
    queryKey: [
      FINDINGS_BY_SIGNAL_ID_QUERY_KEY,
      targetId,
      targetType,
      signalIdFilter,
      findingIdsCacheKey,
      lifecycleState,
    ],
    queryFn: () => {
      if (!targetId) {
        return EMPTY_FINDINGS;
      }

      return fetchFindingsBySignalIdForTarget({
        targetId,
        targetType,
        signalId: signalIdFilter,
        findingIds: hasFindingIds ? findingIds : undefined,
        lifecycleState,
      });
    },
    enabled: !!targetId && hasFilter && enabled,
  });

  const findings = useMemo(() => result.data ?? EMPTY_FINDINGS, [result.data]);

  return {
    findings,
    isLoading: result.isLoading,
    error: result.error,
    refetch: result.refetch,
  };
};
